

interface ApiErrorItem {
  message: {
    ar: string;
    en: string;
  };
  code: string;
}

interface ApiErrorResponse {
  timestamp: string;
  status: number;
  error: string;
  message: string;
  path: string;
  errors?: ApiErrorItem[];
}

/**
 * Extracts the Arabic error message from API error responses
 * @param error - The error caught from API calls
 * @returns The Arabic error message or a default message
 */
export const getErrorMessage = (error: unknown): string => {
  // Default error message in Arabic
  const defaultError = 'حدث خطأ غير متوقع. يرجى المحاولة مرة أخرى.';
  
  try {
    // If it's an Error object
    if (error instanceof Error) {
      // Try to parse the error message as JSON
      try {
        const errorText = error.message;
        
        // Check if the error message contains JSON (from your API)
        if (errorText.includes('{') && errorText.includes('}')) {
          // Extract the JSON part
          const jsonMatch = errorText.match(/\{.*\}/);
          if (jsonMatch) {
            const errorResponse = JSON.parse(jsonMatch[0]) as ApiErrorResponse;
            
            // Extract Arabic message from errors array if it exists
            if (errorResponse.errors && errorResponse.errors.length > 0) {
              return errorResponse.errors[0].message.ar;
            }
          }
        }
        
        // If we couldn't extract a structured error, return the original message
        return error.message;
      } catch {
        // If parsing failed, return the original error message
        return error.message;
      }
    }
    
    // Try to handle as plain string
    if (typeof error === 'string') {
      return error;
    }
    
    // Try to handle as a raw object (could be from response.text())
    if (typeof error === 'object' && error !== null) {
      // Try to access the error structure directly
      const errorObj = error as any;
      
      if (errorObj.errors && Array.isArray(errorObj.errors) && errorObj.errors.length > 0) {
        if (errorObj.errors[0].message && errorObj.errors[0].message.ar) {
          return errorObj.errors[0].message.ar;
        }
      }
    }
    
    return defaultError;
  } catch {
    return defaultError;
  }
};

/**
 * Enhanced version of the API fetch method that handles error extraction
 * @param url - The API URL
 * @param options - Fetch options
 * @returns The parsed response or throws an error with extracted message
 */
export const fetchWithErrorHandling = async (url: string, options: RequestInit): Promise<any> => {
  try {
    const response = await fetch(url, options);
    const responseText = await response.text();
    
    if (!response.ok) {
      // Try to parse the response
      try {
        const errorData = JSON.parse(responseText) as ApiErrorResponse;
        
        // Extract Arabic message if available
        if (errorData.errors && errorData.errors.length > 0 && errorData.errors[0].message?.ar) {
          throw new Error(errorData.errors[0].message.ar);
        }
        
        throw new Error(`${response.status}: ${errorData.message || responseText}`);
      } catch (parseError) {
        if (parseError instanceof Error && parseError.message.includes('ar')) {
          throw parseError;
        }
        throw new Error(`Error ${response.status}: ${responseText}`);
      }
    }
    
    // Try to parse JSON response, or return the raw text if it's not JSON
    try {
      return JSON.parse(responseText);
    } catch {
      return responseText;
    }
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
};